import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { ReactComponent as ColumnIcon } from '@elementary/common-ui/assets/column.svg';
import { ReactComponent as TableIcon } from '@elementary/common-ui/assets/table.svg';
import { ReactComponent as TestIcon } from '@elementary/common-ui/assets/test-icon-small.svg';
import { Flex, FlexColumn } from '@elementary/common-ui/components';
import useHover from '@elementary/common-ui/hooks/useHover';
import { capitalize } from '@elementary/common-ui/utils/common';
import { Typography } from '@mui/material';
import { UseFormWatch } from 'react-hook-form';
import { AddTestForm, ColumnResource, Resource } from 'shared/types';
import { WizardMode } from 'testManagement/types';
import StepperTargetPopup from './StepperTargetPopup';

export const StepsWrapper = styled(Flex)`
  border: 1px solid ${({ theme }) => theme.palette.common.gray8};
`;

const Step = styled(Flex)<{ $selected?: boolean; $disabled?: boolean }>`
  position: relative;
  width: 100%;
  gap: 24px;
  padding: 0 24px;
  height: 72px;
  background: ${({ theme, $selected }) =>
    $selected ? theme.palette.common.gray9 : theme.palette.common.white};
  font-weight: ${({ $selected, theme }) =>
    $selected ? theme.typography.fontWeightBold : 400};
  cursor: ${({ $disabled }) => ($disabled ? 'default' : 'pointer')};
  :hover {
    background: ${({ theme, $disabled }) =>
      !$disabled && theme.palette.common.gray10};
  }
`;

const StepLeft = styled(Flex)`
  gap: 24px;
  align-items: center;
`;

const StepText = styled(Typography)<{
  $selected?: boolean;
  $stepOver?: boolean;
}>`
  font-weight: inherit;
  white-space: nowrap;
  color: ${({ theme, $selected, $stepOver }) =>
    $selected
      ? theme.palette.secondary.main
      : $stepOver
      ? theme.palette.common.black
      : theme.palette.common.slate};
`;

const StepSubText = styled(Typography)`
  font-size: 13px;
  color: ${({ theme }) => theme.palette.common.gray12};
`;

const StepDetail = styled(Flex)`
  gap: 8px;
  align-items: center;
`;

const StepIndex = styled(StepText)<{
  $selected?: boolean;
  $stepOver?: boolean;
}>`
  font-size: 24px;
  color: ${({ theme, $selected }) =>
    $selected ? theme.palette.secondary.main : theme.palette.common.slate};
`;

const Divider = styled.div`
  background: ${({ theme }) => theme.palette.common.gray8};
  height: 100%;
  width: 1px;
  min-width: 1px;
`;

const testTypeNameMap: {
  [key in AddTestForm['testType']]: string;
} = {
  freshnessAnomalies: 'Freshness anomalies',
  cloudFreshnessAnomalies: 'Cloud freshness anomalies',
  eventFreshnessAnomalies: 'Event freshness anomalies',
  volumeAnomalies: 'Volume anomalies',
  cloudVolumeAnomalies: 'Cloud volume anomalies',
  dimensionAnomalies: 'Dimension anomalies',
  schemaChanges: 'Schema changes',
  cloudDataContract: 'Cloud data contract',
  cloudPipelineTaskPerformance: 'Pipeline task performance',
  unique: 'Unique',
  notNull: 'Not null',
  acceptedValues: 'Accepted values',
  relationship: 'Relationship',
  customQuery: 'Custom Query',
  yamlTest: 'Generic YAML Test',
  '': '',
};

type StepBoxProps = {
  stageIndex: number;
  curStage: number;
  title: string;
  icon?: JSX.Element;
  showDescription?: boolean;
  description?: string;
  renderPopup?: () => JSX.Element;
  disabled?: boolean;
  onClick: () => void;
};

export const StepBox = ({
  stageIndex,
  curStage,
  title,
  icon,
  showDescription,
  description,
  renderPopup,
  disabled,
  onClick,
}: StepBoxProps) => {
  const ref = useRef<any>();
  const isHover = useHover(ref);

  return (
    <Step ref={ref} $selected={stageIndex === curStage} $disabled={disabled}>
      <Flex
        sx={{
          justifyContent: 'space-between',
          alignItems: 'center',
          width: '100%',
        }}
        onClick={disabled ? undefined : onClick}
      >
        <StepLeft>
          <StepIndex
            $selected={stageIndex === curStage}
            $stepOver={stageIndex < curStage}
          >
            {stageIndex}
          </StepIndex>
          <FlexColumn>
            <StepText
              $selected={stageIndex === curStage}
              $stepOver={stageIndex < curStage}
            >
              {title}
            </StepText>
            {showDescription && (
              <StepDetail>
                {icon && React.cloneElement(icon)}
                <StepSubText noWrap>{description}</StepSubText>
              </StepDetail>
            )}
          </FlexColumn>
        </StepLeft>
      </Flex>

      {isHover && renderPopup?.()}
    </Step>
  );
};

type Props = {
  mode: WizardMode;
  stage: number;
  watch: UseFormWatch<AddTestForm>;
  onStageClick: (index: number) => void;
  tableResourcesData: Resource[];
  columnsResourcesData: ColumnResource[];
  editMode?: boolean;
};

const TestDialogStepper = ({
  mode,
  stage,
  watch,
  onStageClick,
  tableResourcesData,
  columnsResourcesData,
  editMode,
}: Props) => {
  const [previouslyVisitedSteps, setPreviouslyVisitedSteps] = useState(
    new Set<number>(),
  );

  useEffect(() => {
    setPreviouslyVisitedSteps(new Set(previouslyVisitedSteps.add(stage)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stage]);

  const formatYamlTemplateName = (val: string) =>
    capitalize(val.split('.')[1]?.replaceAll('_', ' '));

  const testDescription =
    watch('testType') === 'yamlTest'
      ? formatYamlTemplateName(watch('yamlTemplateName') || '')
      : testTypeNameMap[watch('testType')];

  return (
    <StepsWrapper divider={<Divider />}>
      {editMode ? (
        <>
          <StepBox
            stageIndex={1}
            curStage={stage}
            title="Test configuration & attributes"
            onClick={() => onStageClick(1)}
          />
          <StepBox
            stageIndex={2}
            curStage={stage}
            title="Review & submit"
            disabled={stage <= 2}
            onClick={() => onStageClick(2)}
          />
        </>
      ) : mode === 'customQuery' ? (
        <>
          <StepBox
            stageIndex={1}
            curStage={stage}
            title="Query formatting"
            onClick={() => onStageClick(1)}
          />
          <StepBox
            stageIndex={2}
            curStage={stage}
            title="Test configuration & attributes"
            disabled={stage <= 2}
            onClick={() => onStageClick(2)}
          />
          <StepBox
            stageIndex={3}
            curStage={stage}
            title="Review & submit"
            disabled={stage <= 3}
            onClick={() => onStageClick(3)}
          />
        </>
      ) : (
        <>
          <StepBox
            stageIndex={1}
            curStage={stage}
            title="Select target"
            icon={
              watch('resourceType') === 'tables' ? (
                <TableIcon />
              ) : (
                <ColumnIcon />
              )
            }
            showDescription={watch('resources')?.length > 0}
            description={`${watch('resources')?.length} ${capitalize(
              watch('resourceType'),
            )} selected`}
            renderPopup={
              stage > 1
                ? () => (
                    <StepperTargetPopup
                      watch={watch}
                      tableResourcesData={tableResourcesData}
                      columnsResourcesData={columnsResourcesData}
                    />
                  )
                : undefined
            }
            onClick={() => onStageClick(1)}
          />
          <StepBox
            stageIndex={2}
            curStage={stage}
            title="Select a test"
            icon={<TestIcon />}
            showDescription={!!watch('testType')}
            description={testDescription}
            disabled={stage <= 2}
            onClick={() => onStageClick(2)}
          />
          <StepBox
            stageIndex={3}
            curStage={stage}
            title="Test configuration & attributes"
            disabled={stage <= 3}
            onClick={() => onStageClick(3)}
          />
          <StepBox
            stageIndex={4}
            curStage={stage}
            title="Review & submit"
            disabled={stage <= 4}
            onClick={() => onStageClick(4)}
          />
        </>
      )}
    </StepsWrapper>
  );
};

export default TestDialogStepper;
